import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, MapPin, Path, ListBullets, WhatsappLogo, ArrowSquareOut, Export, DotsThreeVertical } from "@phosphor-icons/react";
import L from "leaflet";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { pinIcon, haversineKm, formatDistance } from "@/lib/map";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function FitBounds({ showrooms }) {
  const map = useMap();
  useEffect(() => {
    if (showrooms.length === 0) return;
    if (showrooms.length === 1) {
      map.setView([showrooms[0].lat, showrooms[0].lng], 14);
      return;
    }
    const bounds = L.latLngBounds(showrooms.map((s) => [s.lat, s.lng]));
    map.fitBounds(bounds, { paddingTopLeft: [60, 150], paddingBottomRight: [60, 160] });
  }, [showrooms, map]);
  return null;
}

function waNumber(phone) {
  let p = (phone || "").replace(/\D/g, "");
  if (p.startsWith("0")) p = "62" + p.slice(1);
  return p;
}

function waMessage(s) {
  const lines = [
    `Hello${s.contact_name ? " " + s.contact_name : ""}, I'm visiting ${s.name} for the INERRE showroom tour.`,
  ];
  if (s.categories?.length) {
    lines.push("", "Items to look at:", ...s.categories.map((c, i) => `${i + 1}. ${c}`));
  }
  return lines.join("\n");
}

export default function ClientTour() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);

  useEffect(() => {
    api
      .get(`/share/${token}`)
      .then(({ data }) => setData(data))
      .catch(() => setError("This tour link is invalid or has been removed."));
  }, [token]);

  const active = useMemo(
    () => data?.showrooms?.find((s) => s.id === activeId) || null,
    [data, activeId]
  );

  const distances = useMemo(() => {
    if (!data || !active) return [];
    return data.showrooms
      .filter((s) => s.id !== active.id)
      .map((s) => ({ ...s, km: haversineKm(active, s) }))
      .sort((a, b) => a.km - b.km);
  }, [data, active]);

  const checkedFor = (sid) => data?.checked?.[sid] || [];

  const totals = useMemo(() => {
    if (!data) return { done: 0, all: 0 };
    let all = 0;
    let done = 0;
    for (const s of data.showrooms) {
      all += s.categories.length;
      done += (data.checked?.[s.id] || []).filter((c) => s.categories.includes(c)).length;
    }
    return { done, all };
  }, [data]);

  const toggle = async (showroomId, category) => {
    const current = checkedFor(showroomId).includes(category);
    const checked = !current;
    setData((d) => {
      const prev = d.checked?.[showroomId] || [];
      const next = checked ? [...prev, category] : prev.filter((c) => c !== category);
      return { ...d, checked: { ...d.checked, [showroomId]: next } };
    });
    try {
      await api.post(`/share/${token}/check`, { showroom_id: showroomId, category, checked });
    } catch {
      setData((d) => {
        const prev = d.checked?.[showroomId] || [];
        const next = !checked ? [...prev, category] : prev.filter((c) => c !== category);
        return { ...d, checked: { ...d.checked, [showroomId]: next } };
      });
      toast.error("Couldn't save your progress. Check your connection.");
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6" data-testid="tour-error">
        <div className="max-w-md rounded-2xl border border-border bg-card p-10 text-center">
          <p className="font-serif text-3xl font-light">Tour unavailable</p>
          <p className="mt-3 text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Preparing your tour…</p>
      </div>
    );
  }

  const center = data.showrooms.length
    ? [data.showrooms[0].lat, data.showrooms[0].lng]
    : [-6.2088, 106.8456];

  return (
    <div className="relative h-screen w-full overflow-hidden" data-testid="client-tour-page">
      {/* Full-screen map */}
      <MapContainer center={center} zoom={12} className="absolute inset-0 z-0 h-full w-full" zoomControl={false}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds showrooms={data.showrooms} />
        {data.showrooms.map((s, i) => (
          <Marker
            key={s.id}
            position={[s.lat, s.lng]}
            icon={pinIcon(i + 1, { active: activeId === s.id })}
            eventHandlers={{
              click: () => {
                setActiveId(s.id);
                setListOpen(false);
              },
            }}
          >
            <Tooltip direction="top" offset={[0, -28]}>
              <span className="font-medium">{s.name}</span>
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>

      {/* Top glass header */}
      <div className="pointer-events-none absolute left-0 right-0 top-0 z-10 p-4 md:p-6">
        <div className="pointer-events-auto flex items-center justify-between rounded-2xl border border-white/40 bg-white/90 px-6 py-4 backdrop-blur-xl">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              INERRE bespoke
            </p>
            <h1 className="truncate font-serif text-2xl font-light tracking-tight md:text-3xl" data-testid="tour-client-name">
              Showroom Tour
            </h1>
            <p className="mt-0.5 truncate text-xs text-muted-foreground" data-testid="tour-prepared-for">
              Prepared for {data.name}
            </p>
          </div>
          <div className="ml-4 flex items-center gap-3">
            <div className="text-right" data-testid="tour-progress">
              <p className="text-sm font-semibold">
                {totals.done}<span className="text-muted-foreground">/{totals.all}</span>
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">seen</p>
            </div>
            <Button
              data-testid="open-showroom-list"
              variant="outline"
              size="sm"
              onClick={() => {
                setListOpen(true);
                setActiveId(null);
              }}
              className="rounded-full bg-white"
            >
              <ListBullets className="mr-2 h-4 w-4" /> Stops
            </Button>
          </div>
        </div>
        {totals.all > 0 && (
          <div className="mx-auto mt-3 h-1 max-w-xs overflow-hidden rounded-full bg-white/60">
            <div
              className="h-full rounded-full bg-accent transition-[width] duration-500"
              style={{ width: `${(totals.done / totals.all) * 100}%` }}
            />
          </div>
        )}
      </div>

      {/* Floating save-to-home-screen button */}
      {!active && !listOpen && (
        <button
          data-testid="install-app-button"
          onClick={() => setInstallOpen(true)}
          aria-label="Save to home screen"
          style={{ bottom: "calc(6rem + env(safe-area-inset-bottom))" }}
          className="absolute right-4 z-10 flex h-14 w-14 items-center justify-center rounded-full border border-border bg-white/95 shadow-lg backdrop-blur-xl transition-transform duration-200 hover:scale-105 md:right-6 md:!bottom-8"
        >
          <img src="/logo-inerre.png" alt="INERRE" className="h-9 w-9 rounded-full object-contain" />
        </button>
      )}

      <Dialog open={installOpen} onOpenChange={setInstallOpen}>
        <DialogContent className="rounded-2xl" data-testid="install-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl font-light tracking-tighter">
              Save to Home Screen
            </DialogTitle>
            <DialogDescription>
              Open this tour anytime like an app, with the INERRE icon.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div className="rounded-xl border border-border bg-secondary/50 p-4">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                <Export className="h-4 w-4" /> iPhone / iPad
              </p>
              <p className="mt-2 text-sm leading-relaxed">
                Open in <span className="font-semibold">Safari</span> → tap the{" "}
                <span className="font-semibold">Share</span> button →{" "}
                <span className="font-semibold">"Add to Home Screen"</span>.
              </p>
            </div>
            <div className="rounded-xl border border-border bg-secondary/50 p-4">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                <DotsThreeVertical className="h-4 w-4" weight="bold" /> Android
              </p>
              <p className="mt-2 text-sm leading-relaxed">
                Open in <span className="font-semibold">Chrome</span> → tap the{" "}
                <span className="font-semibold">⋮ menu</span> →{" "}
                <span className="font-semibold">"Add to Home screen"</span> or{" "}
                <span className="font-semibold">"Install app"</span>.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* All stops sheet */}
      <AnimatePresence>
        {listOpen && (
          <motion.div
            key="stops"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="absolute bottom-0 left-0 right-0 z-20 mx-auto max-h-[70vh] w-full overflow-y-auto rounded-t-3xl border border-white/40 bg-white/95 p-6 backdrop-blur-xl md:bottom-6 md:left-auto md:right-6 md:w-[420px] md:rounded-3xl"
            data-testid="stops-sheet"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl font-light tracking-tight">
                {data.showrooms.length} Stop{data.showrooms.length === 1 ? "" : "s"}
              </h2>
              <Button data-testid="close-stops-sheet" variant="ghost" size="sm" onClick={() => setListOpen(false)} className="rounded-full">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-4 space-y-2">
              {data.showrooms.map((s, i) => {
                const done = checkedFor(s.id).filter((c) => s.categories.includes(c)).length;
                return (
                  <button
                    key={s.id}
                    data-testid={`stop-item-${s.id}`}
                    onClick={() => {
                      setActiveId(s.id);
                      setListOpen(false);
                    }}
                    className="flex w-full items-center gap-4 rounded-2xl border border-border bg-card p-4 text-left transition-colors duration-200 hover:border-primary/40"
                  >
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-serif text-lg font-light">{s.name}</span>
                      <span className="block text-xs text-muted-foreground">
                        {done}/{s.categories.length} items seen
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active showroom bottom sheet */}
      <AnimatePresence>
        {active && (
          <motion.div
            key={active.id}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="absolute bottom-0 left-0 right-0 z-20 mx-auto max-h-[75vh] w-full overflow-y-auto rounded-t-3xl border border-white/40 bg-white/95 p-6 backdrop-blur-xl md:bottom-6 md:left-6 md:right-auto md:w-[440px] md:rounded-3xl md:p-8"
            data-testid="showroom-sheet"
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Stop {data.showrooms.findIndex((s) => s.id === active.id) + 1} of {data.showrooms.length}
                </p>
                <h2 className="mt-1 font-serif text-3xl font-light tracking-tighter" data-testid="sheet-showroom-name">
                  {active.name}
                </h2>
                {active.address && (
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" /> {active.address}
                  </p>
                )}
              </div>
              <Button data-testid="close-showroom-sheet" variant="ghost" size="sm" onClick={() => setActiveId(null)} className="rounded-full">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <a
                data-testid="open-google-maps"
                href={active.gmaps_url || `https://www.google.com/maps/search/?api=1&query=${active.lat},${active.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-xs font-semibold text-foreground transition-colors duration-200 hover:border-primary/50"
              >
                <ArrowSquareOut className="h-3.5 w-3.5 text-accent" /> Open in Google Maps
              </a>
              {active.contact_phone && (
                <a
                  data-testid="whatsapp-button"
                  href={`https://wa.me/${waNumber(active.contact_phone)}?text=${encodeURIComponent(waMessage(active))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-xs font-semibold text-white transition-transform duration-200 hover:scale-[1.03]"
                >
                  <WhatsappLogo className="h-3.5 w-3.5" weight="fill" /> WhatsApp {active.contact_name || "Contact"}
                </a>
              )}
            </div>

            {active.notes && (
              <p className="mt-4 rounded-xl bg-secondary/70 px-4 py-3 text-sm italic text-muted-foreground">
                {active.notes}
              </p>
            )}

            {/* Checklist */}
            <div className="mt-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                What to look at
              </p>
              {active.categories.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">No items listed for this stop.</p>
              ) : (
                <div className="mt-3 space-y-1.5">
                  {active.categories.map((cat, i) => {
                    const checked = checkedFor(active.id).includes(cat);
                    return (
                      <motion.button
                        key={cat}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        data-testid={`checklist-item-${cat}`}
                        onClick={() => toggle(active.id, cat)}
                        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-150 hover:bg-secondary/60"
                      >
                        <span className={`check-circle ${checked ? "checked" : ""}`}>
                          <Check className="h-3.5 w-3.5 text-white" weight="bold" />
                        </span>
                        <span
                          className={`text-sm font-medium transition-colors duration-200 ${
                            checked ? "text-muted-foreground line-through" : "text-foreground"
                          }`}
                        >
                          {cat}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Distances */}
            {distances.length > 0 && (
              <div className="mt-6 border-t border-border pt-5">
                <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  <Path className="h-3.5 w-3.5" /> Distance to other stops
                </p>
                <div className="mt-3 space-y-2" data-testid="distances-list">
                  {distances.map((d) => (
                    <button
                      key={d.id}
                      data-testid={`distance-to-${d.id}`}
                      onClick={() => setActiveId(d.id)}
                      className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors duration-150 hover:bg-secondary/60"
                    >
                      <span className="truncate font-medium">{d.name}</span>
                      <span className="ml-3 flex-shrink-0 font-semibold text-accent">
                        {formatDistance(d.km)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
